import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface ActionRecouvrement {
  id: number;
  type: string;
  description: string;
  montant_promis?: number;
  date_promesse?: string;
  created_at: string;
  utilisateur: { name: string };
}

interface Litige {
  description: string;
  created_at: string;
}

interface Relance {
  niveau: number;
  date_envoi: string;
}

interface Facture {
  id: number;
  numero: string;
  date_facture: string;
  date_echeance: string;
  total_ttc: number;
  montant_paye: number;
  notes?: string;
  client: {
    raison_sociale: string;
    email: string;
  };
  actions?: ActionRecouvrement[];  // ← Optionnel
  litige?: Litige | null;          // ← Optionnel
  relances?: Relance[];            // ← Optionnel
}

export default function CollectionManagement() {
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedFacture, setSelectedFacture] = useState<Facture | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showLitigeModal, setShowLitigeModal] = useState(false);

  const [actionType, setActionType] = useState<'appel' | 'email' | 'courrier' | 'promesse' | 'relance' | 'mise_en_demeure'>('appel');
  const [actionDescription, setActionDescription] = useState('');
  const [montantPromis, setMontantPromis] = useState('');
  const [datePromesse, setDatePromesse] = useState('');
  const [litigeDescription, setLitigeDescription] = useState('');

  const [filter, setFilter] = useState<'toutes' | 'retard' | 'litige'>('toutes');

  /* ================= FETCH FACTURES IMPAYÉES ================= */
  useEffect(() => {
    const fetchFactures = async () => {
      setLoading(true);
      try {
        const res = await getResources(`factures-client/factures`);
        setFactures(res.data.data || []);
      } catch {
        toast.error('Erreur lors du chargement des factures impayées');
      } finally {
        setLoading(false);
      }
    };
    fetchFactures();
  }, []);

  /* ================= FILTRES ================= */
  const filteredFactures = useMemo(() => {
    let filtered = factures;

    if (filter === 'retard') {
      filtered = filtered.filter(f => new Date(f.date_echeance) < new Date());
    } else if (filter === 'litige') {
      filtered = filtered.filter(f => !!f.litige);
    }

    return filtered.sort((a, b) => {
      const daysA = Math.max(0, new Date().getTime() - new Date(a.date_echeance).getTime()) / (1000 * 3600 * 24);
      const daysB = Math.max(0, new Date().getTime() - new Date(b.date_echeance).getTime()) / (1000 * 3600 * 24);
      return daysB - daysA;
    });
  }, [factures, filter]);

  /* ================= STATS ================= */
  const stats = useMemo(() => {
    const impayees = filteredFactures;
    const enRetard = impayees.filter(f => new Date(f.date_echeance) < new Date());
    const enLitige = impayees.filter(f => !!f.litige);

    const montantTotalImpaye = impayees.reduce((s, f) => s + (f.total_ttc - f.montant_paye), 0);
    const montantRetard = enRetard.reduce((s, f) => s + (f.total_ttc - f.montant_paye), 0);
    const montantLitige = enLitige.reduce((s, f) => s + (f.total_ttc - f.montant_paye), 0);

    const delaiMoyen = enRetard.length > 0
      ? Math.round(enRetard.reduce((s, f) => {
          const days = Math.max(0, (new Date().getTime() - new Date(f.date_echeance).getTime()) / (1000 * 3600 * 24));
          return s + days;
        }, 0) / enRetard.length)
      : 0;

    return {
      totalImpaye: montantTotalImpaye,
      enRetardMontant: montantRetard,
      enLitigeMontant: montantLitige,
      nbRetard: enRetard.length,
      nbLitige: enLitige.length,
      delaiMoyen
    };
  }, [filteredFactures]);

  
  /* ================= ACTIONS ================= */
  const handleAjouterAction = async () => {
    if (!selectedFacture || !actionDescription.trim()) {
      toast.error('Description obligatoire');
      return;
    }
    const TYPE_MAP = {
      appel: 'CALL',
      email: 'EMAIL',
      courrier: 'LETTER',
      promesse: 'PROMISE',
      relance: 'DEMAND',
      mise_en_demeure: 'DEMAND',
      accord: 'AGREEMENT',
    } as const;
    const mappedType = TYPE_MAP[actionType];

    if (!mappedType) {
      toast.error("Type d'action invalide");
      return;
    }

    try {
      await createResource(
        `factures-client/${selectedFacture.id}/action`,
        {
          type: mappedType,
          description: actionDescription,
          montant_promis: montantPromis ? Number(montantPromis) : null,
          date_promesse: datePromesse || null,
        }
      );

      toast.success('Action enregistrée');
      setShowActionModal(false);
      setActionDescription('');
      setMontantPromis('');
      setDatePromesse('');

      // Refresh
      const res = await getResources(`factures-client/factures`);
      setFactures(res.data.data || []);
    } catch {
      toast.error('Erreur lors de l’enregistrement');
    }
  };

  const handleMarquerLitige = async () => {
    if (!selectedFacture || !litigeDescription.trim()) {
      toast.error('Description obligatoire');
      return;
    }

    try {
      await createResource(
        `factures-client/${selectedFacture.id}/litige`,
        {
          description: litigeDescription,
        }
      );

      toast.success('Facture marquée en litige');
      setShowLitigeModal(false);
      setLitigeDescription('');

      // Refresh
      const res = await getResources(`factures-client/factures`);
      setFactures(res.data.data || []);
    } catch {
      toast.error('Erreur');
    }
  };

  const handleEnvoyerRelance = async (facture: Facture) => {
  try {
    await createResource(
      `factures-client/${facture.id}/relance`,{}
    );

    toast.success('Relance envoyée');

    const res = await getResources(`factures-client/factures`);
    setFactures(res.data.data || []);
  } catch (e: any) {
    toast.error(e.response?.data?.message || 'Erreur lors de l’envoi');
  }
};

  const handlePrintFacture = (facture: Facture) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error("Impossible d'ouvrir la fenêtre d'impression");
    return;
  }

  const totalTTC = Number(facture.total_ttc);
  const totalHT = facture.total_ht ?? totalTTC / 1.1925;
  const tvaMontant = facture.total_tva ?? totalTTC - totalHT;
  const montantPaye = Number(facture.montant_paye || 0);
  const resteAPayer = totalTTC - montantPaye;

  const lignes = facture.bon_livraison?.lignes || [];

  // Historique des actions
  const actions = (facture.actions_recouvrement || []).sort((a: any, b: any) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  // Relances
  const relances = (facture.relances || []).sort((a: any, b: any) => 
    new Date(b.date_envoi).getTime() - new Date(a.date_envoi).getTime()
  );

  const logoUrl = "/images/logo.png"; // ← Mets ton logo dans public/images/logo.png

  const html = `<!DOCTYPE html>
  <html lang="fr">
  <head>
    <meta charset="utf-8">
    <title>Facture ${facture.numero} - Suivi Recouvrement</title>
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; background: #f9fafb; color: #333; }
      .container { max-width: 900px; margin: 40px auto; background: white; padding: 40px; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
      .header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 20px; border-bottom: 4px solid #2563eb; margin-bottom: 30px; }
      .logo { max-height: 90px; max-width: 280px; object-fit: contain; }
      .title-block h1 { color: #1e40af; margin: 0; font-size: 32px; font-weight: bold; }
      .title-block h2 { color: #1e3a8a; margin: 10px 0 0; font-size: 24px; }

      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin: 30px 0; }
      .info-block h3 { color: #1e40af; border-bottom: 2px solid #dbeafe; padding-bottom: 8px; margin-bottom: 15px; font-size: 18px; }

      table { width: 100%; border-collapse: collapse; margin: 30px 0; font-size: 15px; }
      th, td { border: 1px solid #e5e7eb; padding: 14px; text-align: left; }
      th { background: #eff6ff; color: #1e40af; font-weight: 600; }
      .text-right { text-align: right; }
      .text-center { text-align: center; }

      .totals { float: right; width: 420px; margin-top: 30px; font-size: 16px; }
      .total-line { display: flex; justify-content: space-between; padding: 10px 0; border-top: 1px solid #e5e7eb; }
      .total-final { font-size: 20px; font-weight: bold; color: #1e40af; border-top: 3px double #2563eb; padding-top: 12px; }

      .section { margin-top: 50px; page-break-inside: avoid; }
      .section h3 { color: #1e40af; font-size: 20px; margin-bottom: 20px; border-bottom: 2px solid #dbeafe; padding-bottom: 8px; }

      .action-item { border-left: 4px solid #2563eb; padding-left: 16px; margin-bottom: 16px; background: #f8fafc; padding: 16px; border-radius: 8px; }
      .action-header { font-weight: bold; color: #1e40af; }
      .action-meta { font-size: 13px; color: #666; margin-top: 4px; }

      .litige-alert { background: #fdf2f8; border-left: 5px solid #c026d3; padding: 20px; border-radius: 8px; margin: 20px 0; }

      .signature-section { margin-top: 100px; page-break-inside: avoid; clear: both; }
      .signature-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 100px; margin-top: 40px; }
      .signature-block { text-align: center; }
      .signature-label { font-weight: bold; font-size: 17px; margin-bottom: 20px; color: #1e40af; }
      .signature-line { border-top: 2px dotted #444; height: 130px; margin: 20px 0; }

      .footer { margin-top: 120px; text-align: center; color: #6b7280; font-size: 12px; clear: both; }

      @media print { 
        body { background: white; margin: 0; } 
        .container { box-shadow: none; padding: 20px; border-radius: 0; } 
      }
    </style>
  </head>
  <body>
    <div class="container">

      <!-- HEADER -->
      <div class="header">
        <img src="${logoUrl}" alt="Logo entreprise" class="logo">
        <div class="title-block">
          <h1>FACTURE & SUIVI RECOUVREMENT</h1>
          <h2>N° ${facture.numero}</h2>
        </div>
      </div>

      <!-- INFOS CLIENT & FACTURE -->
      <div class="info-grid">
        <div class="info-block">
          <h3>Client</h3>
          <p><strong>${facture.client.raison_sociale}</strong></p>
          <p>${facture.client.email || 'Non renseigné'}</p>
        </div>
        <div class="info-block">
          <h3>Facture</h3>
          <p><strong>Date :</strong> ${new Date(facture.date_facture).toLocaleDateString('fr-FR')}</p>
          <p><strong>Échéance :</strong> ${new Date(facture.date_echeance).toLocaleDateString('fr-FR')}</p>
          ${facture.bonLivraison ? `<p><strong>BL :</strong> ${facture.bonLivraison.numero}</p>` : ''}
          <p><strong>Reste dû :</strong> <span style="color:#dc2626;font-weight:bold;">${resteAPayer.toLocaleString('fr-FR')} GNF</span></p>
        </div>
      </div>

      <!-- ARTICLES -->
      <table>
        <thead>
          <tr>
            <th>Désignation</th>
            <th class="text-center">Qté</th>
            <th class="text-right">P.U. HT</th>
            <th class="text-right">Total HT</th>
          </tr>
        </thead>
        <tbody>
          ${lignes.length > 0 ? lignes.map((l: any) => `
            <tr>
              <td>${l.produit?.designation || 'Produit'}</td>
              <td class="text-center">${l.quantite}</td>
              <td class="text-right">${Number(l.prix_unitaire_ht).toLocaleString('fr-FR')} GNF</td>
              <td class="text-right">${Number(l.total_ht || l.quantite * l.prix_unitaire_ht).toLocaleString('fr-FR')} GNF</td>
            </tr>
          `).join('') : '<tr><td colspan="4" class="text-center text-gray-500 py-8">Aucune ligne</td></tr>'}
        </tbody>
      </table>

      <!-- TOTAUX -->
      <div class="totals">
        <div class="total-line"><span>Total HT :</span><span>${totalHT.toLocaleString('fr-FR')} GNF</span></div>
        <div class="total-line"><span>TVA (19,25%) :</span><span>${tvaMontant.toLocaleString('fr-FR')} GNF</span></div>
        <div class="total-line total-final"><span>Total TTC :</span><span>${totalTTC.toLocaleString('fr-FR')} GNF</span></div>
        ${montantPaye > 0 ? `
          <div class="total-line" style="color:#16a34a;font-weight:bold"><span>Montant payé :</span><span>${montantPaye.toLocaleString('fr-FR')} GNF</span></div>
          <div class="total-line" style="color:#dc2626;font-weight:bold"><span>Reste à payer :</span><span>${resteAPayer.toLocaleString('fr-FR')} GNF</span></div>
        ` : ''}
      </div>
      <div style="clear:both;"></div>

      <!-- LITIGE -->
      ${facture.litige ? `
        <div class="litige-alert">
          <p class="font-bold text-purple-900 text-lg">⚠️ FACTURE EN LITIGE</p>
          <p class="mt-2">${facture.litige.description}</p>
          <p class="text-sm text-gray-600 mt-2">Déclaré le ${new Date(facture.litige.created_at).toLocaleDateString('fr-FR')}</p>
        </div>
      ` : ''}

      <!-- HISTORIQUE DES RELANCES -->
      ${relances.length > 0 ? `
        <div class="section">
          <h3>Historique des relances</h3>
          <div class="space-y-3">
            ${relances.map((r: any) => `
              <div class="action-item">
                <p class="action-header">Relance niveau ${r.niveau} — ${new Date(r.date_envoi).toLocaleDateString('fr-FR')}</p>
                <p class="action-meta">Envoyée par système</p>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

      <!-- HISTORIQUE DES ACTIONS -->
      <div class="section">
        <h3>Historique des actions de recouvrement</h3>
        ${actions.length > 0 ? `
          <div class="space-y-4">
            ${actions.map((a: any) => `
              <div class="action-item">
                <p class="action-header">${a.type} — ${new Date(a.created_at).toLocaleDateString('fr-FR')} à ${new Date(a.created_at).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}</p>
                <p class="mt-2">${a.description}</p>
                ${a.montant_promis ? `<p class="text-green-700 font-medium mt-2">Promesse de paiement : ${a.montant_promis.toLocaleString('fr-FR')} GNF pour le ${new Date(a.date_promesse).toLocaleDateString('fr-FR')}</p>` : ''}
                <p class="action-meta mt-3">Par ${a.utilisateur.name}</p>
              </div>
            `).join('')}
          </div>
        ` : '<p class="text-gray-500 italic">Aucune action enregistrée</p>'}
      </div>

      <!-- SIGNATURE -->
      <div class="signature-section">
        <div class="signature-grid">
          <div class="signature-block">
            <div class="signature-label">Émetteur (Vendeur)</div>
            <div class="signature-line"></div>
            <p class="text-sm text-gray-600 mt-4">Cachet + Signature</p>
          </div>
          <div class="signature-block">
            <div class="signature-label">Client (Acheteur)</div>
            <div class="signature-line"></div>
            <p class="text-sm text-gray-600 mt-4">Cachet + Signature</p>
          </div>
        </div>
        <p style="text-align: center; margin-top: 60px; font-style: italic; color: #555;">
          "J'accuse réception de la présente facture et reconnais le suivi de recouvrement."
        </p>
      </div>

      <div class="footer">
        Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}
      </div>

    </div>
  </body>
  </html>`;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 1200);
};
  /* ================= UI HELPERS ================= */
  const getNiveauRelance = (facture: Facture) => {
    const relances = facture.relances || []; // ← Protection
    if (relances.length === 0) return null;
    return Math.max(...relances.map(r => r.niveau));
  };

  const getBadgeRelance = (niveau?: number) => {
    if (!niveau) return null;
    const styles = {
      1: 'bg-blue-100 text-blue-800',
      2: 'bg-orange-100 text-orange-800',
      3: 'bg-red-100 text-red-800',
    };
    const labels = { 1: 'Relance 1', 2: 'Relance 2', 3: 'Mise en demeure' };
    return <span className={`px-3 py-1 text-xs rounded-full font-medium ${styles[niveau] || 'bg-gray-100 text-gray-800'}`}>{labels[niveau] || `Niveau ${niveau}`}</span>;
  };

  const getStatutRetard = (dateEcheance: string) => {
    const days = Math.floor((new Date().getTime() - new Date(dateEcheance).getTime()) / (1000 * 3600 * 24));
    if (days <= 0) return { label: 'À jour', color: 'bg-green-100 text-green-800' };
    if (days <= 30) return { label: `Retard ${days}j`, color: 'bg-yellow-100 text-yellow-800' };
    if (days <= 60) return { label: `Retard ${days}j`, color: 'bg-orange-100 text-orange-800' };
    return { label: `Retard ${days}j`, color: 'bg-red-100 text-red-800' };
  };

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <p className="text-sm text-gray-600">Montant impayé total</p>
          <p className="text-3xl font-bold mt-2">{stats.totalImpaye.toLocaleString('fr-FR')} GNF</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <p className="text-sm text-gray-600">En retard</p>
          <p className="text-3xl font-bold text-orange-600 mt-2">{stats.nbRetard}</p>
          <p className="text-sm text-gray-600">{stats.enRetardMontant.toLocaleString('fr-FR')} GNF</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <p className="text-sm text-gray-600">En litige</p>
          <p className="text-3xl font-bold text-purple-600 mt-2">{stats.nbLitige}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <p className="text-sm text-gray-600">Délai moyen retard</p>
          <p className="text-3xl font-bold mt-2">{stats.delaiMoyen} jours</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setFilter('toutes')}
            className={`px-5 py-2.5 rounded-lg font-medium transition ${filter === 'toutes' ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            Toutes
          </button>
          <button
            onClick={() => setFilter('retard')}
            className={`px-5 py-2.5 rounded-lg font-medium transition ${filter === 'retard' ? 'bg-orange-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            En retard
          </button>
          <button
            onClick={() => setFilter('litige')}
            className={`px-5 py-2.5 rounded-lg font-medium transition ${filter === 'litige' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            En litige
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Facture</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Échéance</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Montant dû</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Relance</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-500">
                    <i className="ri-loader-4-line animate-spin text-3xl"></i>
                    <p className="mt-4">Chargement...</p>
                  </td>
                </tr>
              ) : filteredFactures.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-500">
                    Aucune facture à recouvrer
                  </td>
                </tr>
              ) : (
                filteredFactures.map(f => {
                  const reste = f.total_ttc - f.montant_paye;
                  const statutRetard = getStatutRetard(f.date_echeance);
                  const niveauRelance = getNiveauRelance(f);

                  return (
                    <tr key={f.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div className="font-medium text-blue-600">{f.numero}</div>
                      </td>
                      <td className="px-6 py-4 font-medium">{f.client.raison_sociale}</td>
                      <td className="px-6 py-4">{new Date(f.date_echeance).toLocaleDateString('fr-FR')}</td>
                      <td className="px-6 py-4 text-right font-bold text-red-600">
                        {reste.toLocaleString('fr-FR')} GNF
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${statutRetard.color}`}>
                          {statutRetard.label}
                        </span>
                        {f.litige && (
                          <span className="ml-2 inline-flex px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Litige
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getBadgeRelance(niveauRelance)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-3">
                          <button onClick={() => { setSelectedFacture(f); setShowDetailModal(true); }} title="Détail">
                            <i className="ri-eye-line text-lg text-blue-600 hover:text-blue-800" />
                          </button>
                          <button onClick={() => handleEnvoyerRelance(f)} title="Relance">
                            <i className="ri-mail-send-line text-lg text-orange-600 hover:text-orange-800" />
                          </button>
                          <button onClick={() => { setSelectedFacture(f); setShowActionModal(true); }} title="Action">
                            <i className="ri-add-circle-line text-lg text-indigo-600 hover:text-indigo-800" />
                          </button>
                          <button onClick={() => handlePrintFacture(f)} title="Imprimer">
                            <i className="ri-printer-line text-lg text-gray-600 hover:text-gray-800" />
                          </button>
                          {!f.litige && (
                            <button onClick={() => { setSelectedFacture(f); setShowLitigeModal(true); }} title="Litige">
                              <i className="ri-error-warning-line text-lg text-purple-600 hover:text-purple-800" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* === MODALS (inchangés mais protégés) === */}
      {/* Modal Détail */}
      {showDetailModal && selectedFacture && (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
    <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-5xl w-full max-h-[95vh] overflow-y-auto">

      {/* ================= HEADER ================= */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button
              onClick={() => setShowDetailModal(false)}
              className="w-12 h-12 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center justify-center transition shadow-sm"
              title="Fermer"
            >
              <i className="ri-arrow-left-line text-xl text-gray-600"></i>
            </button>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">
                Suivi Recouvrement – Facture {selectedFacture.numero}
              </h2>
              <p className="text-lg text-gray-600 mt-1">
                Client : <strong>{selectedFacture.client.raison_sociale}</strong>
              </p>
            </div>
          </div>
          <button
            onClick={() => handlePrintFacture(selectedFacture)}
            className="bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-3 shadow-lg transition"
          >
            <i className="ri-printer-line text-xl"></i>
            Imprimer le suivi
          </button>
        </div>
      </div>

      {/* ================= CONTENU ================= */}
      <div className="p-8 space-y-10">

        {/* ================= FACTURE ================= */}
        <div>
          <h3 className="text-2xl font-bold text-blue-900 mb-6 flex items-center gap-3">
            <i className="ri-file-text-line text-3xl"></i>
            Détail de la facture
          </h3>

          {/* Infos client + facture */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
              <h4 className="font-bold text-blue-900 mb-3">Client</h4>
              <p className="text-lg"><strong>{selectedFacture.client.raison_sociale}</strong></p>
              <p className="text-gray-700">{selectedFacture.client.email || 'Non renseigné'}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-300">
              <h4 className="font-bold text-gray-800 mb-3">Informations facture</h4>
              <p><strong>Date :</strong> {new Date(selectedFacture.date_facture).toLocaleDateString('fr-FR')}</p>
              <p><strong>Échéance :</strong> {new Date(selectedFacture.date_echeance).toLocaleDateString('fr-FR')}</p>
              {selectedFacture.bonLivraison && (
                <p><strong>BL :</strong> {selectedFacture.bonLivraison.numero}</p>
              )}
              <p className="mt-4 text-xl font-bold text-red-600">
                Reste à payer : {(selectedFacture.total_ttc - selectedFacture.montant_paye).toLocaleString('fr-FR')} GNF
              </p>
            </div>
          </div>

          {/* Tableau articles */}
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-blue-900 uppercase">Désignation</th>
                  <th className="px-6 py-4 text-center text-sm font-bold text-blue-900 uppercase">Qté</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-blue-900 uppercase">P.U. HT</th>
                  <th className="px-6 py-4 text-right text-sm font-bold text-blue-900 uppercase">Total HT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(selectedFacture.bon_livraison?.lignes || []).length > 0 ? (
                  selectedFacture.bon_livraison?.lignes.map((ligne: any, i: number) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">{ligne.produit?.designation || 'Produit'}</td>
                      <td className="px-6 py-4 text-center">{ligne.quantite}</td>
                      <td className="px-6 py-4 text-right">{Number(ligne.prix_unitaire_ht).toLocaleString('fr-FR')} GNF</td>
                      <td className="px-6 py-4 text-right font-semibold">
                        {Number(ligne.total_ht || ligne.quantite * ligne.prix_unitaire_ht).toLocaleString('fr-FR')} GNF
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-12 text-gray-500">
                      Aucune ligne disponible
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totaux */}
          <div className="flex justify-end mt-8">
            <div className="w-full max-w-md bg-gray-50 rounded-xl p-6 space-y-3 border border-gray-200">
              <div className="flex justify-between text-lg">
                <span>Total HT</span>
                <span className="font-semibold">{Number(selectedFacture.total_ht || 0).toLocaleString('fr-FR')} GNF</span>
              </div>
              <div className="flex justify-between text-lg">
                <span>TVA (19,25%)</span>
                <span className="font-semibold">{Number(selectedFacture.total_tva || 0).toLocaleString('fr-FR')} GNF</span>
              </div>
              <div className="flex justify-between text-2xl font-bold text-blue-600 border-t-2 border-blue-200 pt-4">
                <span>Total TTC</span>
                <span>{Number(selectedFacture.total_ttc).toLocaleString('fr-FR')} GNF</span>
              </div>
              {selectedFacture.montant_paye > 0 && (
                <>
                  <div className="flex justify-between text-lg text-green-600 pt-4 border-t">
                    <span>Montant payé</span>
                    <span className="font-bold">{Number(selectedFacture.montant_paye).toLocaleString('fr-FR')} GNF</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-red-600">
                    <span>Reste à payer</span>
                    <span>{(selectedFacture.total_ttc - selectedFacture.montant_paye).toLocaleString('fr-FR')} GNF</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ================= LITIGE ================= */}
        {selectedFacture.litige && (
          <div className="bg-purple-50 border-l-4 border-purple-600 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <i className="ri-error-warning-fill text-4xl text-purple-600"></i>
              <div>
                <h4 className="text-xl font-bold text-purple-900 mb-2">Facture en litige</h4>
                <p className="text-gray-800">{selectedFacture.litige.description}</p>
                <p className="text-sm text-gray-600 mt-2">
                  Déclaré le {new Date(selectedFacture.litige.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ================= HISTORIQUE RELANCES ================= */}
        {(selectedFacture.relances || []).length > 0 && (
          <div>
            <h3 className="text-2xl font-bold text-orange-900 mb-6 flex items-center gap-3">
              <i className="ri-mail-send-line text-3xl"></i>
              Historique des relances
            </h3>
            <div className="space-y-4">
              {selectedFacture.relances.map((r: any) => (
                <div key={r.id} className="bg-orange-50 border-l-4 border-orange-500 p-5 rounded-r-xl">
                  <p className="font-bold text-orange-900">
                    Relance niveau {r.niveau} — {new Date(r.date_envoi).toLocaleDateString('fr-FR')}
                  </p>
                  <p className="text-sm text-gray-700 mt-1">Envoyée automatiquement</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================= HISTORIQUE ACTIONS ================= */}
        <div>
          <h3 className="text-2xl font-bold text-indigo-900 mb-6 flex items-center gap-3">
            <i className="ri-history-line text-3xl"></i>
            Historique des actions manuelles
          </h3>
          {(selectedFacture.actions_recouvrement || []).length > 0 ? (
            <div className="space-y-5">
              {selectedFacture.actions_recouvrement.map((a: any) => (
                <div key={a.id} className="bg-gradient-to-r from-indigo-50 to-white border-l-4 border-indigo-600 p-6 rounded-r-xl shadow-sm">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-indigo-900 text-lg">
                        {a.type} — {new Date(a.created_at).toLocaleDateString('fr-FR')} à {new Date(a.created_at).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                      </p>
                      <p className="text-gray-800 mt-2 text-base">{a.description}</p>
                      {a.montant_promis && (
                        <p className="mt-3 px-4 py-2 bg-green-100 text-green-800 rounded-lg inline-block font-medium">
                          Promesse de paiement : {Number(a.montant_promis).toLocaleString('fr-FR')} GNF pour le {new Date(a.date_promesse).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">Par <strong>{a.utilisateur.name}</strong></p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">Aucune action manuelle enregistrée</p>
            </div>
          )}
        </div>

      </div>

      {/* ================= FOOTER ================= */}
      <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Document consulté le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
        </p>
        <button
          onClick={() => setShowDetailModal(false)}
          className="px-8 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-xl font-medium transition"
        >
          Fermer
        </button>
      </div>

    </div>
  </div>
)}

      {/* Les autres modals (Action, Litige) restent identiques */}
      {/* ================= MODAL AJOUT ACTION ================= */}
      {showActionModal && selectedFacture && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-lg w-full">

            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <i className="ri-add-circle-line text-4xl text-indigo-600"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Nouvelle action de recouvrement</h3>
                    <p className="text-gray-600 mt-1">Facture {selectedFacture.numero}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowActionModal(false)}
                  className="p-3 hover:bg-gray-100 rounded-lg transition"
                >
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type d'action *</label>
                <select
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value as any)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="appel">Appel téléphonique</option>
                  <option value="email">Email</option>
                  <option value="courrier">Courrier postal</option>
                  <option value="promesse">Promesse de paiement</option>
                  <option value="relance">Relance manuelle</option>
                  <option value="mise_en_demeure">Mise en demeure</option>
                  <option value="accord">Accord / arrangement</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  rows={5}
                  value={actionDescription}
                  onChange={(e) => setActionDescription(e.target.value)}
                  placeholder="Détail de l'action effectuée..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {actionType === 'promesse' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Montant promis</label>
                    <input
                      type="number"
                      value={montantPromis}
                      onChange={(e) => setMontantPromis(e.target.value)}
                      placeholder="0.00"
                      step="0.01"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date promise</label>
                    <input
                      type="date"
                      value={datePromesse}
                      onChange={(e) => setDatePromesse(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-4">
              <button
                onClick={() => setShowActionModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition"
              >
                Annuler
              </button>
              <button
                onClick={handleAjouterAction}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition flex items-center gap-2"
              >
                <i className="ri-check-line"></i>
                Enregistrer l'action
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ================= MODAL MARQUER EN LITIGE ================= */}
      {showLitigeModal && selectedFacture && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-lg w-full">

            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <i className="ri-error-warning-fill text-4xl text-purple-600"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Marquer en litige</h3>
                    <p className="text-gray-600 mt-1">Facture {selectedFacture.numero}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLitigeModal(false)}
                  className="p-3 hover:bg-gray-100 rounded-lg transition"
                >
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Attention : cette action est irréversible sans intervention manuelle.
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motif du litige *
              </label>
              <textarea
                rows={6}
                value={litigeDescription}
                onChange={(e) => setLitigeDescription(e.target.value)}
                placeholder="Décrivez précisément le motif du litige..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-4">
              <button
                onClick={() => setShowLitigeModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition"
              >
                Annuler
              </button>
              <button
                onClick={handleMarquerLitige}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition flex items-center gap-2"
              >
                <i className="ri-alert-line"></i>
                Confirmer le litige
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}